import { isRunningTests } from '@glow/common'
import { is } from 'immutable'
import { useSelector } from 'react-redux'
import { createSelectorCreator, lruMemoize } from 'reselect'
import { AppStateType } from '../utils/appStateReduxStore'
import {
  captureDefinition,
  instrumentedIs,
  instrumentSelector,
  registerSelectorStatsConsoleApi
} from './selectorInstrumentation'

const createPlainSelector = createSelectorCreator(lruMemoize, is)
const createRecordingSelector = createSelectorCreator(lruMemoize, instrumentedIs)

type SelectorCreator = typeof createPlainSelector

// Instrumented only under jest, which is where the benchmarks in test/bench run; there
// `glowSelectorStats` attributes wasted deep walks and call counts to a selector. Enabling it
// here rather than disabling it is deliberate: `captureDefinition` takes a stack per selector
// at module load whether or not anything is recording, and no served build should pay that for
// a tool that has to be started by hand.
const instrumentationEnabled = isRunningTests()

// Object.assign carries over reselect's own properties on the creator, `withTypes` included
const createInstrumentedSelector: SelectorCreator = Object.assign((...args: Parameters<SelectorCreator>) => {
  const { site, frame } = captureDefinition()
  return instrumentSelector(createRecordingSelector(...args), site, frame)
}, createRecordingSelector)

export const createImmutableEqualSelector: SelectorCreator = instrumentationEnabled
  ? createInstrumentedSelector
  : createPlainSelector

if (instrumentationEnabled) registerSelectorStatsConsoleApi()

// For selectors called with different arguments from several places at once, where one cache entry would thrash
export const createCachedImmutableEqualSelector = createSelectorCreator({
  memoize: lruMemoize,
  argsMemoize: lruMemoize,
  memoizeOptions: { equalityCheck: is, maxSize: 10 },
  argsMemoizeOptions: { equalityCheck: is, maxSize: 10 }
})

export const useImmutableSelector = <T>(selector: (state: AppStateType) => T) => useSelector(selector, is)
